import { useState, useRef } from "react";
import videoIcon from "../../assets/icons/video.svg";
import arrowDownIcon from "../../assets/icons/arrow-down.svg";
import arrowUpIcon from "../../assets/icons/arrow-up.svg";
import playIcon from "../../assets/icons/play.svg";
import pauseIcon from "../../assets/icons/pause.svg";
import userIcon from "../../assets/icons/user.svg";
import clockIcon from "../../assets/icons/clock.svg";
import clockFileIcon from "../../assets/icons/clock-file.svg";


import { ReplyModal } from "../ReplyModal";
import {
  Card,
  CardHeader,
  VideoIcon,
  UserInfo,
  UserName,
  RequestDate,
  CategoryTag,
  StatusWrapper,
  StatusBadge,
  ExpandButton,
  ReplyButton,
  VideoContainer,
  Description,
  VideoPlayerWrapper,
  Video,
  VideoControls,
  ControlButton,
  TimeInfo,
  ProgressBarContainer,
  ProgressBar,
} from "./styles";
import { HelpCase } from "../../service/helpCaseService";

const API_URL = process.env.REACT_APP_API_URL;

function CustomVideoPlayer({ src }: { src: string }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hasError, setHasError] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const togglePlayPause = () => {
    if (videoRef.current && !hasError) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(() => setHasError(true));
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleVideoTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
      setDuration(videoRef.current.duration || 0);
    }
  };

  const handleProgressChange = (value: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = value;
      setCurrentTime(value);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  return (
    <VideoPlayerWrapper>
      {hasError ? (
        <div style={{ color: '#ff6b6b', textAlign: 'center', padding: '2rem', background: '#1a1a1a' }}>
          Vídeo indisponível
        </div>
      ) : (
        <>
          <Video
            ref={videoRef}
            src={src}
            onTimeUpdate={handleVideoTimeUpdate}
            onLoadedMetadata={handleVideoTimeUpdate}
            onEnded={handleEnded}
            onError={() => setHasError(true)}
          >
            Seu navegador não suporta o elemento de vídeo.
          </Video>

          <VideoControls>
            <ControlButton onClick={togglePlayPause}>
              {isPlaying ? <img src={pauseIcon} alt="pausar" /> : <img src={playIcon} alt="play" />}
            </ControlButton>

            <TimeInfo>{formatTime(currentTime)}</TimeInfo>

            <ProgressBarContainer>
              <ProgressBar
                type="range"
                min="0"
                max={duration || 0}
                value={currentTime}
                onChange={(e) => handleProgressChange(Number(e.target.value))}
              />
            </ProgressBarContainer>

            <TimeInfo>{formatTime(duration)}</TimeInfo>
          </VideoControls>
        </>
      )}
    </VideoPlayerWrapper>
  );
}

interface RequestCardProps {
  id: string;
  email: string;
  date: string;
  scenarioName?: string;
  content?: string;
  status: "pendente" | "respondida";
  videoUrl?: string;
  answers: HelpCase[];
  onReplySuccess?: () => void;
}

export function RequestCard({
  id,
  email,
  date,
  scenarioName,
  content,
  status,
  videoUrl,
  answers,
  onReplySuccess,
}: RequestCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isReplyModalOpen, setIsReplyModalOpen] = useState(false);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  return (
    <Card>
      <CardHeader>
        <VideoIcon>
          <img className="video-icon" src={videoIcon} alt="camera" />
        </VideoIcon>

        <UserInfo>
          
          <UserName><img className="user-icon" src={userIcon} alt="usuario" />{email}</UserName>
          <RequestDate><img className="clock-icon" src={clockIcon} alt="data" /> {date}</RequestDate>
        </UserInfo>

        <CategoryTag>{scenarioName}</CategoryTag>

        <StatusWrapper>
          <StatusBadge status={status}>
            {status === "respondida" ? "✓ Respondida" : <><img className="icone status" src={clockFileIcon} alt="clock icon" /> Pendente</>}
          </StatusBadge>
        </StatusWrapper>

        <ReplyButton onClick={() => setIsReplyModalOpen(true)}>
          Responder
        </ReplyButton>

        <ExpandButton onClick={() => setIsExpanded(!isExpanded)}>
          {isExpanded ? <img src={arrowDownIcon} alt="expandir" /> : <img src={arrowUpIcon} alt="arrow up icon" />}
        </ExpandButton>
      </CardHeader>

      {isExpanded && (
        <VideoContainer>
          <Description>
            {content}
          </Description>

          {videoUrl ? (
            <CustomVideoPlayer src={videoUrl} />
          ) : (
            <div style={{ color: 'white', textAlign: 'center', padding: '2rem' }}>
              Nenhum vídeo disponível para esta solicitação
            </div>
          )}

          {answers.map((answer) => (
            <VideoContainer className="answer" key={answer.id}>
              <Description>
                Resposta enviada em: {formatDate(answer.created_at)}
                <br/>
                {answer.content}
              </Description>

              {answer.file_path ? (
                <CustomVideoPlayer src={`${API_URL}/helpcase/file/${answer.id}`} />
              ) : (
                <div style={{ color: 'white', textAlign: 'center', padding: '2rem' }}>
                  Nenhum vídeo disponível nesta resposta
                </div>
              )}
            </VideoContainer>
          ))}
        </VideoContainer>
      )}
      
      <ReplyModal
        isOpen={isReplyModalOpen}
        onClose={() => setIsReplyModalOpen(false)}
        helpCaseId={id}
        email={email}
        date={date}
        scenarioName={scenarioName}
        content={content}
        videoUrl={videoUrl}
        onSuccess={() => {
          setIsReplyModalOpen(false);
          onReplySuccess?.();
        }}
      />
    </Card>
  );
}